import { useEffect, useState } from "react";

import { ArrowTopRightOnSquareIcon, CheckCircleIcon, ExclamationTriangleIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";

import { useConsignmentNotePhoto } from "../../data/useWasteTracking";
import Modal from "../../layouts/Modal";
import { IWasteReceiptRow, WasteReceiptCarrierType, WasteReceiptScanStatus } from "../../models/wasteTracking/IWasteReceiptRow";
import formatDateTime from "../../utils/formatDateTime";
import { IHaulierOption } from "../../utils/hauliers";
import { carrierTypeLabels, formatEwcCode, haulierPresetFromScan, IHaulierPreset, scanStatusLabel } from "../../utils/wasteTracking";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingWheel from "../shared/LoadingWheel";

interface WasteReceiptScanReviewModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** The row whose note is being reviewed. Comes from the table so edits made behind the modal show through. */
  row: IWasteReceiptRow | null;
  haulierOptions: IHaulierOption[];
  /** Read-only view (submitted / excluded tabs). */
  editable: boolean;
  busy: boolean;
  onConfirm: (row: IWasteReceiptRow) => void;
  onRescan: (row: IWasteReceiptRow) => void;
  onPickHaulier: (row: IWasteReceiptRow, haulierAccountId: string) => void;
  onCreateHaulier: (row: IWasteReceiptRow, preset: IHaulierPreset) => void;
}

// The office's side of the yard's "Capture Waste Note": the photo, what the AI read off it, and
// the confirm that lifts the hold on the row. Nothing here writes to the row directly - haulier
// picks go back through the table's commit so the row stays the single source of truth.
export default function WasteReceiptScanReviewModal({
  open,
  setOpen,
  row,
  haulierOptions,
  editable,
  busy,
  onConfirm,
  onRescan,
  onPickHaulier,
  onCreateHaulier,
}: WasteReceiptScanReviewModalProps) {
  const scan = row?.consignmentScan ?? null;
  const result = scan?.result ?? null;
  const photo = useConsignmentNotePhoto(open ? scan?.documentId : null, "WasteReceiptScanReviewModal");

  // Object URLs have to be released by hand, so the blob becomes a URL here and is revoked when
  // the photo changes or the modal unmounts.
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!photo.data) {
      setPhotoUrl(null);
      return;
    }
    const url = URL.createObjectURL(photo.data);
    setPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photo.data]);

  if (!row || !scan) return null;

  const awaiting = scan.status === WasteReceiptScanStatus.AwaitingReview;
  const failed = scan.status === WasteReceiptScanStatus.Failed;
  const pending = scan.status === WasteReceiptScanStatus.Pending;
  const preset = haulierPresetFromScan(result);
  const matchedOption = result?.haulierMatch ? haulierOptions.find((o) => o.value === result.haulierMatch?.accountId) : undefined;
  const carrier = result?.carrier;
  const ewcMismatch = result?.ewcCodeMatchesExpected === false;

  const detail = (label: string, value: React.ReactNode) => (
    <div className="grid grid-cols-3 gap-2 py-1.5 text-sm">
      <dt className="text-gray-500">{label}</dt>
      <dd className="col-span-2 text-gray-900">{value ?? <span className="text-gray-400">Not read</span>}</dd>
    </div>
  );

  return (
    <Modal width="max-w-5xl" open={open} setOpen={setOpen}>
      <div className="flex items-start gap-3">
        <SparklesIcon className="mt-0.5 h-6 w-6 flex-none text-violet-500" />
        <div>
          <h1 className="text-xl">Consignment note - {row.vrm ?? "vehicle"}</h1>
          <p className="text-sm text-gray-500">
            Captured by the yard {formatDateTime(scan.scannedOn, DateTime.DATETIME_SHORT, "")}
            {scan.confirmedOn && <> · confirmed {formatDateTime(scan.confirmedOn, DateTime.DATETIME_SHORT, "")}</>}
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-6 md:grid-cols-2">
        {/* Photo */}
        <div>
          <div className="flex min-h-[20rem] items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-50">
            {photo.isLoading && <LoadingWheel />}
            {photo.isError && <p className="p-4 text-sm text-red-700">The photo could not be loaded.</p>}
            {photoUrl && <img src={photoUrl} alt={`Consignment note for ${row.vrm ?? "vehicle"}`} className="max-h-[32rem] w-full object-contain" />}
          </div>
          {photoUrl && (
            <button
              type="button"
              className="mt-2 inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 hover:underline"
              onClick={() => window.open(photoUrl, "_blank", "noopener")}
            >
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" /> Open full size
            </button>
          )}
        </div>

        {/* What the AI read */}
        <div className="space-y-4">
          <div
            className={
              awaiting
                ? "rounded-md border border-violet-200 bg-violet-50 p-3 text-sm text-violet-900"
                : failed
                  ? "rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800"
                  : "rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700"
            }
          >
            <p className="font-medium">{scanStatusLabel(scan)}</p>
            {awaiting && (
              <p className="mt-1">
                The highlighted cells on this row were filled in by AI from this photo. Check them against the note, correct anything
                that is wrong in the table, then confirm. The vehicle cannot be submitted to DEFRA until it is confirmed.
              </p>
            )}
            {pending && <p className="mt-1">The AI is still reading this note. Refresh the page in a moment.</p>}
            {failed && (
              <p className="mt-1">
                The photo is filed against the vehicle, but nothing could be read off it{scan.error ? `: ${scan.error}` : "."} Enter the details
                in the table by hand, or retry the read.
              </p>
            )}
            {scan.status === WasteReceiptScanStatus.Confirmed && <p className="mt-1">The office has checked this note against the row.</p>}
          </div>

          {result && (
            <dl className="divide-y divide-gray-100 rounded-md border border-gray-200 px-3">
              {detail(
                "Consignment note code",
                result.code ? (
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-mono">{result.code}</span>
                    {!result.isValidFormat && (
                      <span className="inline-flex items-center gap-1 text-xs text-yellow-700">
                        <ExclamationTriangleIcon className="h-3.5 w-3.5" /> Not a recognised format - not applied
                      </span>
                    )}
                    {result.confidence && <span className="text-xs text-gray-500">{result.confidence} confidence</span>}
                  </span>
                ) : null
              )}
              {detail(
                "EWC code on the note",
                result.ewcCode ? (
                  <span className="flex flex-wrap items-center gap-2">
                    <span>{formatEwcCode(result.ewcCode)}</span>
                    {ewcMismatch ? (
                      <span className="inline-flex items-center gap-1 text-xs text-yellow-700">
                        <ExclamationTriangleIcon className="h-3.5 w-3.5" /> Wilma submits every vehicle as 16 01 04 - check the paperwork
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-green-700">
                        <CheckCircleIcon className="h-3.5 w-3.5" /> Matches 16 01 04
                      </span>
                    )}
                  </span>
                ) : null
              )}
              {detail(
                "Carrier",
                result.carrierIsOwn ? (
                  <span>
                    {carrierTypeLabels[WasteReceiptCarrierType.OwnCarrier]}
                    {carrier?.registrationNumber && <span className="text-gray-500"> · {carrier.registrationNumber}</span>}
                  </span>
                ) : carrier?.hasDetails ? (
                  <span className="space-y-0.5">
                    <span className="block">{carrier.organisationName ?? <span className="text-gray-400">Name not read</span>}</span>
                    {carrier.registrationNumber && <span className="block font-mono text-xs text-gray-600">{carrier.registrationNumber}</span>}
                    {[carrier.address, carrier.postcode].filter(Boolean).length > 0 && (
                      <span className="block text-xs text-gray-500">{[carrier.address, carrier.postcode].filter(Boolean).join(", ")}</span>
                    )}
                    {[carrier.phone, carrier.email].filter(Boolean).length > 0 && (
                      <span className="block text-xs text-gray-500">{[carrier.phone, carrier.email].filter(Boolean).join(" · ")}</span>
                    )}
                  </span>
                ) : null
              )}
              {carrier?.vehicleRegistration && detail("Carrier vehicle reg", <span className="uppercase">{carrier.vehicleRegistration}</span>)}
              {result.notes && detail("AI notes", <span className="text-gray-600">{result.notes}</span>)}
            </dl>
          )}

          {/* Haulier outcome */}
          {result && !result.carrierIsOwn && carrier?.hasDetails && (
            <div className="rounded-md border border-gray-200 p-3 text-sm">
              {result.haulierMatch ? (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p>
                    <CheckCircleIcon className="mr-1 inline h-4 w-4 text-green-600" />
                    Matched haulier account <span className="font-medium">{matchedOption?.label ?? result.haulierMatch.name}</span>
                    <span className="text-xs text-gray-500"> (by {result.haulierMatch.matchedBy})</span>
                  </p>
                  {editable && row.haulierAccountId !== result.haulierMatch.accountId && (
                    <ButtonInput
                      label="Link this haulier"
                      isSubmit={false}
                      size="sm"
                      disabled={busy}
                      onClick={() => onPickHaulier(row, result.haulierMatch!.accountId)}
                      classes={undefined}
                    />
                  )}
                </div>
              ) : preset ? (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p>
                    <ExclamationTriangleIcon className="mr-1 inline h-4 w-4 text-yellow-600" />
                    No haulier account matches <span className="font-medium">{preset.name}</span>.
                  </p>
                  {editable && (
                    <ButtonInput
                      label="Create haulier account"
                      isSubmit={false}
                      size="sm"
                      disabled={busy}
                      onClick={() => onCreateHaulier(row, preset)}
                      classes={undefined}
                    />
                  )}
                </div>
              ) : null}
              {editable && !result.haulierMatch && haulierOptions.length > 0 && (
                <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                  <span>Or link an existing haulier:</span>
                  <select
                    className="rounded-md border border-gray-300 p-1 text-xs"
                    value=""
                    disabled={busy}
                    onChange={(e) => e.target.value && onPickHaulier(row, e.target.value)}
                  >
                    <option value="">Select…</option>
                    {haulierOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap justify-end gap-2">
        <ButtonInput label="Close" isSubmit={false} onClick={() => setOpen(false)} classes="bg-gray-500 hover:bg-gray-600" />
        {editable && !pending && (
          <ButtonInput label="Retry AI read" isSubmit={false} disabled={busy} onClick={() => onRescan(row)} classes="bg-gray-600 hover:bg-gray-700" />
        )}
        {editable && awaiting && <ButtonInput label="Confirm details" isSubmit={false} isLoading={busy} onClick={() => onConfirm(row)} classes={undefined} />}
      </div>
    </Modal>
  );
}
